import { DropdownMenuProps } from '@radix-ui/react-dropdown-menu';
import { Root as Portal } from '@radix-ui/react-portal';
import classNames from 'classnames';
import { Icon, IconsEnum } from 'components/Icons';
import { ScommesseTableSubtitle } from 'components/scommesseTable/scommesseTableSubtitle';
import { useIsAvvenimento } from 'features/sport/hooks';
import { useBlockScroll } from 'hooks/useBlockScroll';
import useOutsideAlerter from 'hooks/useClickOutside';
import useWindowSize from 'hooks/useWindowSize';
import { SizeInfoAggiuntiva } from 'lib/api/sport/sportScommesseBySlugResponse';
import React, { Dispatch, SetStateAction, useEffect, useMemo, useRef, useState } from 'react';
import { ItemWithTranslation } from 'types/swagger';
import { isClientSide } from 'utility/constant';
import { isNotEmpty } from 'utility/functions';
import listingStyles from '../../../wrappers/scommesseListing/ScommesseListing.module.scss';
import { Leaf, LeafProps } from '../../cells';
import { ScrollPositionContext } from '../context/ScrollPositionContext';
import tableStyles from '../ScommesseTableQuotaSelect.module.scss';
import { ScommesseTableQuotaSelectMenu } from '../scommesseTableQuotaSelectMenu';
import styles from './ScommesseTableQuotaSelectContainer.module.scss';

export type ScommesseTableQuotaSelectProps = ItemWithTranslation<{ selectedValue?: string }> &
  LeafProps &
  DropdownMenuProps & {
    size?: SizeInfoAggiuntiva | string;
    isOpen: boolean;
    counter?: number;
    disabled?: boolean;
    children: React.ReactNode;
    isEstesa?: boolean;
    className?: string;
    containerClassName?: string;
    showHeader?: boolean;
    headerList: Array<string | undefined>;
    setIsOpen: Dispatch<SetStateAction<boolean>>;
  };

export const ScommesseTableQuotaSelectContainer = ({
  size,
  type,
  //  header,
  isOpen,
  counter,
  children,
  isEstesa,
  disabled,
  className,
  containerClassName,
  showHeader,
  //  headerTrKey,
  headerList,
  itemKey,
  scommessaKey,
  infoAggiuntivaKey,
  // selectedValue,
  // selectedValueTrKey,
  setIsOpen,
}: ScommesseTableQuotaSelectProps) => {
  const body = useRef<HTMLBodyElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const { isMobile } = useWindowSize();
  const isAvvenimento = useIsAvvenimento();

  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
  const [scrollPosition, setScrollPosition] = useState(0);

  useEffect(() => {
    body.current = document.querySelector('body');
  }, []);

  const [isRight, setIsRight] = useState<boolean>(true);
  const [isBottom, setIsBottom] = useState<boolean>(true);

  const resetModal = () => {
    setIsModalVisible(false);
    setIsOpen(false);
    setIsBottom(true);
    setIsRight(true);
  };

  useOutsideAlerter({
    ref: containerRef,
    onClickOutside: () => {
      if (!isMobile) {
        setTimeout(() => {
          resetModal();
        }, 0);
      }
    },
    deps: [isMobile],
  });

  useBlockScroll(isMobile && isOpen);

  const calculateMenuPosition = () =>
    setTimeout(() => {
      if (isClientSide && dropdownRef.current) {
        const overflowAttribute = document.body.style.overflowY;
        document.body.style.overflowY = 'auto'; // NOTE: ADDED THIS FOR PREVENT BLOCKING SCROLL FROM RADIX PORTALS

        const offset = 24;
        const windowRight = window.innerWidth;
        const windowBottom = window.innerHeight;

        const parent = document.querySelectorAll('[data-parent]')[0];
        const bottomMenu = dropdownRef.current.getBoundingClientRect().bottom!;
        const rightMenu = dropdownRef.current.getBoundingClientRect().right!;

        setIsBottom(
          parent
            ? bottomMenu! < parent?.getBoundingClientRect().bottom! && bottomMenu! < windowBottom - offset
            : bottomMenu! < windowBottom - offset
        );

        setIsRight(
          parent
            ? rightMenu! < parent?.getBoundingClientRect().right! && rightMenu! < windowRight
            : rightMenu! < windowRight
        );

        setIsModalVisible(true);

        document.body.style.overflowY = overflowAttribute;
      }
    }, 0); // WAY TO HAVE DROPDOWN REF

  const handleClick = () => {
    if (!isMobile) {
      if (containerRef.current) {
        setScrollPosition(window.scrollY);
        resetModal();
        setIsOpen(!isOpen);
        calculateMenuPosition();
      }
    } else {
      setIsOpen(true);
      setScrollPosition(window.scrollY);
      setIsModalVisible(true);
    }
  };

  const onClose = () => {
    window.scrollTo(0, scrollPosition);
    setIsOpen(false);
  };

  const index = counter ?? 0;

  const objChewron = useMemo(
    () => (
      <Icon
        iconId={IconsEnum.CHEVRON_DOWN}
        className={classNames(styles.icon, { [styles.rotate]: isOpen })}
        color="var( --color-on-bg-secondary)"
      />
    ),
    [isOpen]
  );

  if (disabled) {
    // TODO : fare componente a parte -> infoAggItem
    return (
      <div
        className={classNames(styles.box, containerClassName && containerClassName, {
          [styles.heightBox]: showHeader && !isAvvenimento,
        })}
      >
        {showHeader && isNotEmpty(headerList?.[index]) && (
          <ScommesseTableSubtitle className={styles.header}>{headerList?.[index]}</ScommesseTableSubtitle>
        )}
        <div className={classNames(tableStyles.singleBlock, { [styles.estesa]: isEstesa })} data-kind="betting-option">
          <p className={listingStyles.paragraph}>
            <span
              className={classNames(
                tableStyles.selectedQuota,
                { [styles.estesa]: isEstesa, [styles[size!]]: !isAvvenimento },
                listingStyles.label
              )}
            >
              <Leaf type={type} itemKey={itemKey} scommessaKey={scommessaKey} infoAggiuntivaKey={infoAggiuntivaKey} />
            </span>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className={classNames(styles.box, containerClassName && containerClassName, {
        [styles.heightBox]: showHeader && !isAvvenimento,
      })}
    >
      {showHeader && isNotEmpty(headerList?.[index]) && (
        <ScommesseTableSubtitle className={styles.header}>{headerList?.[index]}</ScommesseTableSubtitle>
      )}
      <ScrollPositionContext.Provider value={{ scrollPosition }}>
        <div
          className={classNames(
            styles.wrapper,
            className ? className : '',
            size && !isAvvenimento ? styles[size] : '',
            { [styles.estesa]: isEstesa },
            { [styles.withHeader]: showHeader }
          )}
        >
          <div ref={containerRef} className={styles.containerSelect}>
            <button type="button" className={styles.button} onClick={handleClick} ref={buttonRef}>
              <span className={classNames(styles.text, size && !isAvvenimento ? styles[size] : '')}>
                <Leaf type={type} itemKey={itemKey} scommessaKey={scommessaKey} infoAggiuntivaKey={infoAggiuntivaKey} />
              </span>
              <span className={styles.iconContainer}>{objChewron}</span>
            </button>
            {isOpen && (
              <Portal asChild container={isMobile ? body.current : containerRef.current}>
                <div className={classNames(styles.wrapperMenu, { [styles.visible]: isModalVisible })}>
                  <ScommesseTableQuotaSelectMenu
                    ref={dropdownRef}
                    // TODO label da cms?
                    title={'Scegli la tua quota'}
                    isRight={isRight}
                    isBottom={isBottom}
                    handleClose={onClose}
                  >
                    {children}
                  </ScommesseTableQuotaSelectMenu>
                </div>
              </Portal>
            )}
          </div>
        </div>
      </ScrollPositionContext.Provider>
    </div>
  );
};
